import { Injectable, Optional } from '@angular/core';
import { Configuration } from '../../models/workflow/configuration';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { ConfigurationService } from '../../../entry-routes/configuration/configuration.service';
import { Url } from '../../models/url';
import { Observable } from 'rxjs';
import { ProcessInstanceResponse } from '../../models/workflow/processInstanceResponse';
import { CustomHttpUrlEncodingCodec } from '../../models/workflow/customHttpUrlEncodingCodecEncoder';
import { ProcessInstanceCreateRequest } from '../../models/workflow/processInstanceCreateRequest';
import { DataResponseProcessDefinitionResponse } from '../../models/workflow/dataResponseProcessDefinitionResponse';
import { ProcessDefinitionResponse } from '../../models/workflow/processDefinitionResponse';

@Injectable({
  providedIn: 'root'
})
export class WkfProcessInstanceService {
  protected basePath ;
  public configuration = new Configuration();

  constructor(
    private httpClient: HttpClient,
    private configAction: ConfigurationService,
    @Optional() configuration: Configuration
  ) {
    if (configuration) {
      this.configuration = configuration;
    }
  }

  private getBasePath(): string {
    if (!this.basePath) {
      const flowableProtectedApiBaseUrl = Url.getFlowableProtectedApiBaseUrl(this.configAction);
      console.debug('WkfProcessInstanceService, flowableProtectedApiBaseUrl : '+flowableProtectedApiBaseUrl);
      this.basePath =  flowableProtectedApiBaseUrl + Url.WKF_PROCESS_API;
    }
    console.debug('WkfProcessInstanceService, this.basePath : '+this.basePath);
    return this.basePath;
  }


  /**
   * Start a process instance
   * Note that also a *transientVariables* property is accepted as part of this json, that follows the same structure as the *variables* property.  Only one of *processDefinitionId*, *processDefinitionKey* or *message* can be used in the request body.   Parameters *businessKey*, *variables* and *tenantId* are optional.   If tenantId is omitted, the default tenant will be used. More information about the variable format can be found in the REST variables section.   Note that the variable-scope that is supplied is ignored, process-variables are always local.
   * @param body
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public createProcessInstance(body?: ProcessInstanceCreateRequest, observe?: 'body', reportProgress?: boolean): Observable<ProcessInstanceResponse>;
  public createProcessInstance(body?: ProcessInstanceCreateRequest, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<ProcessInstanceResponse>>;
  public createProcessInstance(body?: ProcessInstanceCreateRequest, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<ProcessInstanceResponse>>;
  public createProcessInstance(body?: ProcessInstanceCreateRequest, observe: any = 'body', reportProgress = false ): Observable<any> {


    let headers= new HttpHeaders();

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'application/json'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [
    ];
    const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected != undefined) {
      headers = headers.set('Content-Type', httpContentTypeSelected);
    }


    return this.httpClient.post<ProcessInstanceResponse>(`${this.basePath}/runtime/process-instances`,
      body,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress
      }
    );
  }

    /**
     * List of process definitions
     *
     * @param version Only return process definitions with the given version.
     * @param name Only return process definitions with the given name.
     * @param nameLike Only return process definitions with a name like the given name.
     * @param key Only return process definitions with the given key.
     * @param keyLike Only return process definitions with a name like the given key.
     * @param resourceName Only return process definitions with the given resource name.
     * @param resourceNameLike Only return process definitions with a name like the given resource name.
     * @param category Only return process definitions with the given category.
     * @param categoryLike Only return process definitions with a category like the given name.
     * @param categoryNotEquals Only return process definitions which do not have the given category.
     * @param deploymentId Only return process definitions with the given category.
     * @param startableByUser Only return process definitions which are part of a deployment with the given id.
     * @param latest Only return the latest process definition versions. Can only be used together with key and keyLike parameters, using any other parameter will result in a 400-response.
     * @param suspended If true, only returns process definitions which are suspended. If false, only active process definitions (which are not suspended) are returned.
     * @param sort Property to sort on, to be used together with the order.
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public listProcessDefinitions(version?: number, name?: string, nameLike?: string, key?: string, keyLike?: string, resourceName?: string, resourceNameLike?: string, category?: string, categoryLike?: string, categoryNotEquals?: string, deploymentId?: string, startableByUser?: string, latest?: boolean, suspended?: boolean, sort?: 'name' | 'id' | 'key' | 'category' | 'deploymentId' | 'version', observe?: 'body', reportProgress?: boolean): Observable<DataResponseProcessDefinitionResponse>
    /*
    public listProcessDefinitions(version?: number, name?: string, nameLike?: string, key?: string, keyLike?: string, resourceName?: string, resourceNameLike?: string, category?: string, categoryLike?: string, categoryNotEquals?: string, deploymentId?: string, startableByUser?: string, latest?: boolean, suspended?: boolean, sort?: 'name' | 'id' | 'key' | 'category' | 'deploymentId' | 'version', observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<DataResponseProcessDefinitionResponse>>;
    public listProcessDefinitions(version?: number, name?: string, nameLike?: string, key?: string, keyLike?: string, resourceName?: string, resourceNameLike?: string, category?: string, categoryLike?: string, categoryNotEquals?: string, deploymentId?: string, startableByUser?: string, latest?: boolean, suspended?: boolean, sort?: 'name' | 'id' | 'key' | 'category' | 'deploymentId' | 'version', observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<DataResponseProcessDefinitionResponse>>;
    public listProcessDefinitions(version?: number, name?: string, nameLike?: string, key?: string, keyLike?: string, resourceName?: string, resourceNameLike?: string, category?: string, categoryLike?: string, categoryNotEquals?: string, deploymentId?: string, startableByUser?: string, latest?: boolean, suspended?: boolean, sort?: 'name' | 'id' | 'key' | 'category' | 'deploymentId' | 'version', observe: any = 'body', reportProgress = false ): Observable<any> */
    {

        //key = 'courrierEntrant';
        latest = true;
        //sort = 'key';


        let queryParameters = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
        if (version !== undefined && version !== null) {
            queryParameters = queryParameters.set('version', <any>version);
        }
        if (name !== undefined && name !== null) {
            queryParameters = queryParameters.set('name', <any>name);
        }
        if (nameLike !== undefined && nameLike !== null) {
            queryParameters = queryParameters.set('nameLike', <any>nameLike);
        }
        if (key !== undefined && key !== null) {
            queryParameters = queryParameters.set('key', <any>key);
        }
        if (keyLike !== undefined && keyLike !== null) {
            queryParameters = queryParameters.set('keyLike', <any>keyLike);
        }
        if (resourceName !== undefined && resourceName !== null) {
            queryParameters = queryParameters.set('resourceName', <any>resourceName);
        }
        if (resourceNameLike !== undefined && resourceNameLike !== null) {
            queryParameters = queryParameters.set('resourceNameLike', <any>resourceNameLike);
        }
        if (category !== undefined && category !== null) {
            queryParameters = queryParameters.set('category', <any>category);
        }
        if (categoryLike !== undefined && categoryLike !== null) {
            queryParameters = queryParameters.set('categoryLike', <any>categoryLike);
        }
        if (categoryNotEquals !== undefined && categoryNotEquals !== null) {
            queryParameters = queryParameters.set('categoryNotEquals', <any>categoryNotEquals);
        }
        if (deploymentId !== undefined && deploymentId !== null) {
            queryParameters = queryParameters.set('deploymentId', <any>deploymentId);
        }
        if (startableByUser !== undefined && startableByUser !== null) {
            queryParameters = queryParameters.set('startableByUser', <any>startableByUser);
        }
        if (latest !== undefined && latest !== null) {
            queryParameters = queryParameters.set('latest', <any>latest);
        }
        if (suspended !== undefined && suspended !== null) {
            queryParameters = queryParameters.set('suspended', <any>suspended);
        }
        if (sort !== undefined && sort !== null) {
            queryParameters = queryParameters.set('sort', <any>sort);
        }

        let headers = new HttpHeaders();

        // authentication (basicAuth) required
        if (this.configuration.username || this.configuration.password) {
            headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
        }

        // to determine the Accept header
        const httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        return this.httpClient.get<DataResponseProcessDefinitionResponse>(Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+`/admin/process-definitions`,
            {
                params: queryParameters,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }


    /**
     * List of tasks definitions
    */
    public listTasksDefinitions(): Observable<DataResponseProcessDefinitionResponse> {


      const queryParameters = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});

      let headers = new HttpHeaders();

      // authentication (basicAuth) required
      if (this.configuration.username || this.configuration.password) {
          headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
      }

      // to determine the Accept header
      const httpHeaderAccepts: string[] = [
          'application/json'
      ];
      const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      if (httpHeaderAcceptSelected != undefined) {
          headers = headers.set('Accept', httpHeaderAcceptSelected);
      }

      return this.httpClient.get<DataResponseProcessDefinitionResponse>(Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+`/admin/task-definitions`,
          {
              params: queryParameters,
              withCredentials: this.configuration.withCredentials,
              headers: headers
          }
      );
    }


    /**
     * Liste des processus autorisé le lancement manuel
    */
    public  listProcessAutorise(currentDomaine: string): Observable<ProcessDefinitionResponse[]> {

      let headers = new HttpHeaders();

      // authentication (basicAuth) required
      if (this.configuration.username || this.configuration.password) {
          headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
      }

      // to determine the Accept header
      const httpHeaderAccepts: string[] = [
          'application/json'
      ];
      const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      if (httpHeaderAcceptSelected != undefined) {
          headers = headers.set('Accept', httpHeaderAcceptSelected);
      }

      return this.httpClient.get<ProcessDefinitionResponse[]>(Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_NEXIA_API+`/process/process-definitions/`+currentDomaine,
          {
              withCredentials: this.configuration.withCredentials,
              headers: headers,
          }
      );
    }


    /**
     * Démerrer un nouveau workflow depuis la liste des tâches
    */
    public  startProcessByKey(processKey: string): Observable<HttpResponse<any>> {

      let headers = new HttpHeaders();

      // authentication (basicAuth) required
      if (this.configuration.username || this.configuration.password) {
          headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
      }

      // to determine the Accept header
      const httpHeaderAccepts: string[] = [
          'application/json'
      ];
      const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      if (httpHeaderAcceptSelected != undefined) {
          headers = headers.set('Accept', httpHeaderAcceptSelected);
      }

      return this.httpClient.post<HttpResponse<any>>(
        Url.getFlowableProtectedApiBaseUrl(this.configAction) + Url.WKF_NEXIA_API + `/process/startProcess/` + processKey,
        null,
        {
          withCredentials: this.configuration.withCredentials,
          headers: headers
        }
      );
    }

}
